import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';

import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { TestPhaseInfoComponent } from 'app/shared/components/test-phase-info/test-phase-info.component';
import { combineLatest, Subject, switchMap, take, takeUntil } from 'rxjs';
import { PaymentService } from '../../../shared/services/payment.service';
import { RightsService } from '../../../shared/services/rights.service';
import { HelpMenuComponent } from '../../common/help-menu/help-menu.component';

@Component({
    selector: 'logged-in-main',
    templateUrl: './logged-in-main.component.html',
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        MatSidenav,
        MatSidenavContainer,
        MatSidenavContent,
        SearchComponent,
        FuseFullscreenComponent,
        HelpMenuComponent,
        UserComponent,
        TestPhaseInfoComponent,
        RouterOutlet,
        MatTooltip,
    ],
})
export class LoggedInMainComponent implements OnInit, OnDestroy {
    private readonly rightsService = inject(RightsService);
    private readonly paymentService = inject(PaymentService);
    private readonly navigationService = inject(NavigationService);
    private readonly fuseMediaWatcherService = inject(FuseMediaWatcherService);
    private readonly router = inject(Router);

    protected readonly isFreeLicense = signal<boolean>(false);
    protected readonly navigation = signal<Navigation | null>(null);
    protected readonly navigationOpened = signal<boolean>(true);

    private readonly mediaChange = toSignal(this.fuseMediaWatcherService.onMediaChange$, {
        initialValue: { matchingAliases: [] as string[], matchingQueries: {} },
    });

    protected readonly isScreenSmall = computed(() => !this.mediaChange().matchingAliases.includes('md'));

    protected readonly isExtension =
        window.location.href.includes('chrome-extension://') ||
        window.parent !== window ||
        (new URLSearchParams(window.location.search).has('mode') &&
            new URLSearchParams(window.location.search).get('mode') === 'extension');

    private readonly unsubscribeAll: Subject<void> = new Subject<void>();

    private readonly NAV_OPENED_KEY = 'mainNavigation.opened';

    constructor() {
        effect(() => {
            if (this.isScreenSmall()) {
                this.navigationOpened.set(false);
            } else {
                this.navigationOpened.set(this.loadNavigationState());
            }
        });
    }

    protected get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        // Load initial basic navigation
        this.navigationService
            .getBasicNavigation()
            .pipe(take(1))
            .subscribe((navigation) => this.navigation.set(navigation));

        // Subscribe to full navigation based on license type and rights
        combineLatest([this.paymentService.getLicenseType(), this.rightsService.getRightsOfCurrentUser()])
            .pipe(
                takeUntil(this.unsubscribeAll),
                switchMap(([licenseType, rights]) => this.navigationService.getFullNavigation(licenseType, rights)),
            )
            .subscribe((navigation) => this.navigation.set(navigation));

        this.router.events
            .pipe(
                takeUntil(this.unsubscribeAll),
            )
            .subscribe((event) => {
                if (event instanceof NavigationEnd && this.isScreenSmall()) {
                    this.navigationOpened.set(false);
                }
            });

        this.paymentService
            .isFreeLicense()
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((isFreeLicense) => {
                this.isFreeLicense.set(isFreeLicense);
            });
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    protected toggleNavigation(): void {
        const newState = !this.navigationOpened();
        this.navigationOpened.set(newState);
        if (!this.isScreenSmall()) {
            try {
                localStorage.setItem(this.NAV_OPENED_KEY, String(newState));
            } catch {
                // Ignore storage failures and keep navigation working without persistence.
            }
        }
    }

    private loadNavigationState(): boolean {
        try {
            const navOpened = localStorage.getItem(this.NAV_OPENED_KEY);
            return navOpened === null || navOpened === 'true';
        } catch {
            return true;
        }
    }
}
